import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
    Modal,
    TextField,
    Divider,
    FormControl,
    FormControlLabel,
    Select,
    InputLabel,
    MenuItem,
    Switch,
    FormHelperText,
} from "@mui/material";
import { debounce } from "lodash";
import { useState, useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import { useAuth } from "../../contexts/contexts";
import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { maskCpfCnpj } from "../../helpers/cpfOrCnpjValidator";
import { phoneMask } from "../../helpers/phone";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { FormatDefaultListProps } from "../../pages/CustomerCreatePage/types";
import { LIST_TYPES_DATABASES } from "../../pages/NewManagerDatabasesPage/constants";
import { HolderProps } from "../../pages/ProposalRegisterPage/types";
import { api } from "../../services/api";
import { syncProjeto704Database } from "../../services/customer/projeto704";
import ButtonGeneric from "../ButtonGeneric";
import { If } from "../If";
import SelectSearch from "../SelectSearch";
import {
    clouds,
    customerDatabaseFields,
    customerDatabaseGridFields,
    customerDatabaseLabels,
    mapCustomerDatabaseToFormValues,
    getNameDatabaseById,
} from "./constants";
import { schemaModalInputs } from "./schema";
import {
    Container,
    TitleModal,
    ContentButtonModal,
    ContentInput,
    Body,
    SubTitle,
    CustomerDatabaseGrid,
    CustomerDatabaseHeader,
    CustomerDatabaseTitle,
    CustomerDatabaseList,
    CustomerDatabaseSection,
} from "./styles";
import {
    ModalEditCustomerProps,
    ModalEditCustomerMode,
    CustomerDatabaseFormKey,
    CustomerDatabaseFormValues,
    SchemaCustomerDatabaseFormValues,
    ICustomerDatabaseProps,
} from "./types";

function ModalEditCustomer({
    visible,
    setVisible,
    datasCustomer,
    getCustomer,
    editMode = "customer",
}: ModalEditCustomerProps) {
    const showCustomerSection = editMode === "customer";
    const showDatabaseSection = editMode === "database";

    const modalTitles: Record<ModalEditCustomerMode, string> = {
        customer: "Editar dados do cliente",
        database: "Editar dados do banco",
    };
    const modalTitle = modalTitles[editMode];
    const handlePortMask = (value: string) => {
        return value.replace(/\D/g, "").slice(0, 5);
    };

    const handleIpMask = (value: string) => {
        return value.replace(/[^0-9.]/g, "").slice(0, 15);
    };

    const { handleSubmit, control, setValue, reset, getValues, formState } =
        useForm<SchemaCustomerDatabaseFormValues>({
            resolver: yupResolver(schemaModalInputs),
            defaultValues: {
                customerDatabase: [],
            },
        });
    const { isDirty } = formState;
    const {
        fields: customerDatabaseFieldsArray,
        replace: replaceCustomerDatabase,
    } = useFieldArray({
        control,
        name: "customerDatabase",
    });
    const { hasPermissions } = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [syncLoading, setSyncLoading] = useState<boolean>(false);
    // const [listClustersMongo, setListClustersMongo] = useState<
    //     ClusterListProps[]
    // >([]);
    // const [listClustersMysql, setListClustersMysql] = useState<
    //     ClusterListProps[]
    // >([]);
    const [listHolder, setListHolder] = useState<FormatDefaultListProps[]>([]);
    const [loadingHolders, setLoadingHolders] = useState<boolean>(false);
    const [holderDocument, setHolderDocument] = useState("");

    const getAllHolder = async (nameHolder: string) => {
        try {
            setLoadingHolders(true);
            const responseAllHolder = await api.get(
                `holders/all?full_name=${nameHolder}`
            );

            if (responseAllHolder.status) {
                const listHoldersFormated =
                    responseAllHolder.data.response.data.map(
                        (value: HolderProps) => {
                            return {
                                value: value.id,
                                label: value.full_name,
                                document: value.document,
                            };
                        }
                    );

                setListHolder(listHoldersFormated);
            }
        } catch (error) {
            console.error(error);
            defaultToast("error", "Erro pegar titulares");
        } finally {
            setLoadingHolders(false);
        }
    };

    const handleGetHolders = debounce((nameHolder: string) => {
        getAllHolder(nameHolder);
    }, 500);

    // const getAllClusters = () => {
    //     const responseAllClustersMongo = api.get(
    //         `clustersDatabases/${mongodb}`
    //     );

    //     const responseAllClustersMysql = api.get(`clustersDatabases/${mysql}`);

    //     Promise.all([responseAllClustersMongo, responseAllClustersMysql]).then(
    //         (response) => {
    //             if (response[0].status) {
    //                 setListClustersMongo(response[0].data);
    //             }

    //             if (response[1].status) {
    //                 setListClustersMysql(response[1].data);
    //             }
    //         }
    //     );
    // };

    const getCustomerDatabaseInfo = async () => {
        if (!datasCustomer?.id) {
            replaceCustomerDatabase([]);
            return;
        }

        try {
            const responseDatabase = await api.get(
                `customers/${datasCustomer?.id}/customer_databases`
            );
            const customerDatabaseResponse = Array.isArray(
                responseDatabase?.data?.response
            )
                ? responseDatabase.data.response
                : [];

            const mappedDatabases = customerDatabaseResponse.map(
                (database: ICustomerDatabaseProps) =>
                    mapCustomerDatabaseToFormValues(database)
            );

            replaceCustomerDatabase(mappedDatabases);
            reset({
                ...getValues(),
                customerDatabase: mappedDatabases,
            });
        } catch (error) {
            console.error(error);
            defaultToast("error", "Erro pegar informações do banco do cliente");
        }
    };

    const buildCustomerDatabasePayload = (
        customerDatabase: CustomerDatabaseFormValues[]
    ) => {
        return customerDatabase.map((database) => {
            const customerDatabaseData = customerDatabaseFields.reduce(
                (acc, field) => {
                    if (field === "customer_database_type") {
                        return acc;
                    }

                    return {
                        ...acc,
                        [field]: database[field],
                    };
                },
                {} as Omit<
                    Pick<CustomerDatabaseFormValues, CustomerDatabaseFormKey>,
                    "customer_database_type"
                >
            );

            return {
                id: database.id,
                ...customerDatabaseData,
                customer_database_type_id: Number(
                    database.customer_database_type
                ),
            };
        });
    };

    const updateCustomerDatabases = async (
        customerDatabase: CustomerDatabaseFormValues[]
    ) => {
        await api.put(
            `customers/${datasCustomer?.id}/customer_databases`,
            buildCustomerDatabasePayload(customerDatabase)
        );
    };

    const handleEditCustomer = async (
        data: SchemaCustomerDatabaseFormValues
    ) => {
        try {
            setLoading(true);

            if (showCustomerSection) {
                await api.put(`customers/${datasCustomer?.id}`, {
                    name: data.nameCompany,
                    project_name: data.projectName,
                    email: data.email,
                    phone: data.phone,
                    managed_database_mysql_id: data.id_cluster_mysql,
                    managed_database_mongo_id: data.id_cluster_mongo,
                    cloud: data.cloud,
                    holder_id: data.holder.value,
                    holder_name: data.holder.label,
                });
            }

            if (showDatabaseSection && customerDatabaseFieldsArray.length > 0) {
                await updateCustomerDatabases(data.customerDatabase);
                reset(data);
            }

            defaultToast(
                "success",
                editMode === "database"
                    ? "Dados do banco atualizados com sucesso!"
                    : "Cliente atualizado com sucesso!"
            );
            getCustomer();

            if (editMode !== "database") {
                setVisible(false);
            }
        } catch (error) {
            console.error(error);
            defaultToast("error", "Erro ao editar cliente");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!visible || !showDatabaseSection) {
            return;
        }

        getCustomerDatabaseInfo();
    }, [datasCustomer?.id, showDatabaseSection, visible]);

    useEffect(() => {
        if (datasCustomer) {
            setValue("cloud", datasCustomer.cloud ?? "");
            setValue("nameCompany", datasCustomer.name);
            setValue("projectName", datasCustomer.project_name ?? "");
            setValue("phone", datasCustomer.phone);
            setValue("email", datasCustomer.email);
            setValue("holder", {
                label: datasCustomer.holder.full_name,
                value: datasCustomer.holder.id,
            });
            setHolderDocument(
                datasCustomer.holder?.document ?? datasCustomer.document ?? ""
            );

            if (datasCustomer.customer_database_mongo) {
                if (datasCustomer.customer_database_mongo.managed_database_id) {
                    setValue(
                        "id_cluster_mongo",
                        datasCustomer.customer_database_mongo
                            .managed_database_id
                    );
                }
            }

            if (datasCustomer.customer_database_mysql) {
                if (datasCustomer.customer_database_mysql.managed_database_id) {
                    setValue(
                        "id_cluster_mysql",
                        datasCustomer.customer_database_mysql
                            .managed_database_id
                    );
                }
            }
        }
    }, [datasCustomer, setValue]);

    const formatCustomerDatabaseLabel = (key: CustomerDatabaseFormKey) => {
        return customerDatabaseLabels[key] ?? key;
    };

    const isInfra704Customer = datasCustomer?.cloud === "infra704";

    const buildSyncSuccessMessage = (
        message: string,
        responseData?: {
            ip_applied?: boolean;
            deployments_started?: number;
            vm?: { ip?: string };
        }
    ) => {
        const parts = [message];

        if (responseData?.deployments_started) {
            parts.push(
                `${responseData.deployments_started} deploy(s) enfileirado(s).`
            );
        }

        if (responseData?.ip_applied && responseData?.vm?.ip) {
            parts.push(`IP da VM: ${responseData.vm.ip}.`);
        }

        return parts.join(" ");
    };

    const handleSyncWithProjeto704 = handleSubmit(
        async (data: SchemaCustomerDatabaseFormValues) => {
            if (!datasCustomer?.id || !isDirty) {
                return;
            }

            try {
                setSyncLoading(true);

                if (customerDatabaseFieldsArray.length > 0) {
                    await updateCustomerDatabases(data.customerDatabase);
                }

                const response = await syncProjeto704Database(datasCustomer.id);

                defaultToast(
                    "success",
                    buildSyncSuccessMessage(
                        response.message,
                        response.response?.data
                    )
                );
                reset(data);
                getCustomer();
            } catch (error) {
                console.error(error);
                defaultToast(
                    "error",
                    getApiErrorMessage(
                        error,
                        "Erro ao sincronizar credenciais com Projeto704"
                    )
                );
            } finally {
                setSyncLoading(false);
            }
        }
    );

    return (
        <Modal
            open={visible}
            onClose={() => setVisible(false)}
            aria-labelledby="modal-customer-edit-title"
            aria-describedby="modal-customer-edit-description"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
            }}
        >
            <Container onSubmit={handleSubmit(handleEditCustomer)}>
                <TitleModal>{modalTitle}</TitleModal>
                <Body>
                    {showCustomerSection && (
                        <>
                            <ContentInput>
                                <Controller
                                    control={control}
                                    name="projectName"
                                    render={({
                                        field: { onChange, value = "" },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            variant="outlined"
                                            label="Nome do projeto"
                                            size="small"
                                            onChange={onChange}
                                            value={value}
                                            fullWidth
                                            error={Boolean(error)}
                                            helperText={error && error?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    control={control}
                                    name="nameCompany"
                                    render={({
                                        field: { onChange, value = "" },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            variant="outlined"
                                            label="Nome"
                                            size="small"
                                            onChange={onChange}
                                            value={value}
                                            fullWidth
                                            error={Boolean(error)}
                                            helperText={error && error?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    control={control}
                                    name="email"
                                    render={({
                                        field: { onChange, value = "" },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            variant="outlined"
                                            label="Email"
                                            size="small"
                                            onChange={onChange}
                                            value={value}
                                            fullWidth
                                            error={Boolean(error)}
                                            helperText={error && error?.message}
                                        />
                                    )}
                                />
                            </ContentInput>

                            <ContentInput>
                                <Controller
                                    control={control}
                                    name="phone"
                                    render={({
                                        field: { value = "" },
                                        fieldState: { error },
                                    }) => (
                                        <TextField
                                            variant="outlined"
                                            label="Telefone"
                                            size="small"
                                            fullWidth
                                            onChange={(event) => {
                                                setValue(
                                                    "phone",
                                                    phoneMask(
                                                        event.target.value
                                                    )
                                                );
                                            }}
                                            value={value}
                                            error={Boolean(error)}
                                            helperText={error && error?.message}
                                            inputProps={{
                                                maxLength: 14,
                                            }}
                                        />
                                    )}
                                />

                                {(holderDocument ||
                                    datasCustomer?.document) && (
                                    <TextField
                                        variant="outlined"
                                        label="Documento"
                                        size="small"
                                        fullWidth
                                        disabled
                                        value={maskCpfCnpj(
                                            holderDocument ||
                                                (datasCustomer?.document as string) ||
                                                ""
                                        )}
                                        inputProps={{
                                            maxLength: 18,
                                        }}
                                    />
                                )}

                                <Controller
                                    control={control}
                                    name="holder"
                                    render={({
                                        field: { value },
                                        fieldState: { error },
                                    }) => (
                                        <SelectSearch
                                            fullWidth
                                            width="400"
                                            label="Titular"
                                            options={listHolder}
                                            placeholder="Tilular"
                                            setValue={(selectedHolder) => {
                                                setValue("holder", {
                                                    label:
                                                        selectedHolder?.label ??
                                                        "",
                                                    value: selectedHolder?.value,
                                                });
                                                setHolderDocument(
                                                    selectedHolder?.document ??
                                                        ""
                                                );
                                            }}
                                            loading={loadingHolders}
                                            onChangeTextField={handleGetHolders}
                                            value={value}
                                            error={Boolean(error)}
                                            textError={error?.message}
                                        />
                                    )}
                                />
                            </ContentInput>

                            <If
                                condition={hasPermissions(
                                    "configuracoes.visualizar_gerenciamento_banco"
                                )}
                            >
                                <ContentInput>
                                    <Controller
                                        control={control}
                                        name="cloud"
                                        render={({
                                            field: { onChange, value },
                                            fieldState: { error },
                                        }) => (
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={Boolean(error)}
                                            >
                                                <InputLabel id="select-cloud">
                                                    Cloud Provider
                                                </InputLabel>
                                                <Select
                                                    labelId="select-cloud"
                                                    id="select-mysql-cluster"
                                                    label="Cloud Provider"
                                                    value={value}
                                                    onChange={onChange}
                                                >
                                                    {clouds.map((value) => {
                                                        return (
                                                            <MenuItem
                                                                key={value}
                                                                value={value}
                                                            >
                                                                {value}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                                {error && (
                                                    <FormHelperText>
                                                        {error.message}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    />
                                </ContentInput>
                            </If>
                        </>
                    )}

                    {showDatabaseSection &&
                        customerDatabaseFieldsArray.length > 0 && (
                            <If
                                condition={hasPermissions(
                                    "configuracoes.visualizar_gerenciamento_banco"
                                )}
                            >
                                <Divider />

                                <SubTitle>Cloud - Banco de dados</SubTitle>

                                <CustomerDatabaseList>
                                    {customerDatabaseFieldsArray.map(
                                        (databaseField, index) => (
                                            <CustomerDatabaseSection
                                                key={databaseField.id}
                                                $hasDivider={index > 0}
                                            >
                                                {index > 0 && (
                                                    <Divider
                                                        flexItem
                                                        sx={{ my: 2 }}
                                                    />
                                                )}

                                                <CustomerDatabaseHeader>
                                                    <CustomerDatabaseTitle>
                                                        {`Banco ${
                                                            index + 1
                                                        } - ${getNameDatabaseById(
                                                            databaseField?.customer_database_type
                                                        )}`}
                                                    </CustomerDatabaseTitle>
                                                    <Controller
                                                        control={control}
                                                        name={
                                                            `customerDatabase.${index}.backup_enabled` as const
                                                        }
                                                        render={({
                                                            field: {
                                                                value: backupValue,
                                                                onChange:
                                                                    onBackupChange,
                                                            },
                                                        }) => (
                                                            <FormControlLabel
                                                                label={formatCustomerDatabaseLabel(
                                                                    "backup_enabled"
                                                                )}
                                                                control={
                                                                    <Switch
                                                                        checked={Boolean(
                                                                            backupValue
                                                                        )}
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            onBackupChange(
                                                                                event
                                                                                    .target
                                                                                    .checked
                                                                            )
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </CustomerDatabaseHeader>

                                                <CustomerDatabaseGrid>
                                                    {customerDatabaseGridFields.map(
                                                        (key) => (
                                                            <Controller
                                                                key={`${databaseField.id}-${key}`}
                                                                control={
                                                                    control
                                                                }
                                                                name={
                                                                    `customerDatabase.${index}.${key}` as const
                                                                }
                                                                render={({
                                                                    field: {
                                                                        value,
                                                                        onChange,
                                                                    },
                                                                }) => {
                                                                    let maxLength:
                                                                        | number
                                                                        | undefined;
                                                                    let inputMode:
                                                                        | "numeric"
                                                                        | "decimal"
                                                                        | undefined;

                                                                    if (
                                                                        key ===
                                                                        "port"
                                                                    ) {
                                                                        maxLength = 5;
                                                                        inputMode =
                                                                            "numeric";
                                                                    } else if (
                                                                        key ===
                                                                        "ip"
                                                                    ) {
                                                                        maxLength = 15;
                                                                        inputMode =
                                                                            "decimal";
                                                                    }

                                                                    if (
                                                                        key ===
                                                                        "customer_database_type"
                                                                    ) {
                                                                        return (
                                                                            <FormControl
                                                                                fullWidth
                                                                                size="small"
                                                                            >
                                                                                <InputLabel
                                                                                    id={`customer-database-type-label-${index}`}
                                                                                >
                                                                                    {formatCustomerDatabaseLabel(
                                                                                        key
                                                                                    )}
                                                                                </InputLabel>
                                                                                <Select
                                                                                    labelId={`customer-database-type-label-${index}`}
                                                                                    id={`customer-database-type-${index}`}
                                                                                    label={formatCustomerDatabaseLabel(
                                                                                        key
                                                                                    )}
                                                                                    value={
                                                                                        value ??
                                                                                        ""
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) => {
                                                                                        onChange(
                                                                                            event
                                                                                                .target
                                                                                                .value
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    {LIST_TYPES_DATABASES.map(
                                                                                        (
                                                                                            databaseType
                                                                                        ) => (
                                                                                            <MenuItem
                                                                                                key={
                                                                                                    databaseType.id
                                                                                                }
                                                                                                value={
                                                                                                    databaseType.id
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    databaseType.name
                                                                                                }
                                                                                            </MenuItem>
                                                                                        )
                                                                                    )}
                                                                                </Select>
                                                                            </FormControl>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <TextField
                                                                            variant="outlined"
                                                                            label={formatCustomerDatabaseLabel(
                                                                                key
                                                                            )}
                                                                            size="small"
                                                                            fullWidth
                                                                            value={
                                                                                value ??
                                                                                ""
                                                                            }
                                                                            onChange={(
                                                                                event
                                                                            ) => {
                                                                                if (
                                                                                    key ===
                                                                                    "port"
                                                                                ) {
                                                                                    onChange(
                                                                                        handlePortMask(
                                                                                            event
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    );
                                                                                    return;
                                                                                }

                                                                                if (
                                                                                    key ===
                                                                                    "ip"
                                                                                ) {
                                                                                    onChange(
                                                                                        handleIpMask(
                                                                                            event
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    );
                                                                                    return;
                                                                                }

                                                                                onChange(
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                );
                                                                            }}
                                                                            inputProps={{
                                                                                maxLength,
                                                                                inputMode,
                                                                            }}
                                                                        />
                                                                    );
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </CustomerDatabaseGrid>
                                            </CustomerDatabaseSection>
                                        )
                                    )}
                                </CustomerDatabaseList>
                            </If>
                        )}
                </Body>

                <ContentButtonModal>
                    <ButtonGeneric
                        onclick={() => setVisible(false)}
                        buttonColor="var(--cancel)"
                        text="CANCELAR"
                        className="cancelButton"
                    />
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={loading}
                    >
                        Confirmar
                    </LoadingButton>
                    {showDatabaseSection &&
                        isInfra704Customer &&
                        hasPermissions(
                            "configuracoes.visualizar_gerenciamento_banco"
                        ) && (
                            <LoadingButton
                                variant="outlined"
                                loading={syncLoading}
                                disabled={!isDirty || loading}
                                onClick={handleSyncWithProjeto704}
                            >
                                Sincronizar com o Projeto704
                            </LoadingButton>
                        )}
                </ContentButtonModal>
            </Container>
        </Modal>
    );
}

export default ModalEditCustomer;
